// UnmappedStrip — banner + collapsible list of Entra-linked Persons with
// zero team memberships (PRODOPSAI-512).
//
// Data is sourced from `treeData.unmapped` in the existing `/tree` response
// (US3 ships both arrays in one round-trip). No separate API call here.
//
// "Map to team →" is a STUB for US7 (PRODOPSAI-510): the page wires
// `onMapPerson` to a no-op (console.log) for now; US7 will swap it for
// `openDialog({personId})`.

import { useEffect, useMemo, useState } from 'react';
import type { IUnmappedPerson } from '@skystone/shared';
import { Button } from '@/components/ui';
import { ChevronDownIcon, ChevronRightIcon } from '@/components/ui/icons';

interface UnmappedStripProps {
  persons: IUnmappedPerson[];
  /** Triggered when the user clicks "Map to team →" on a row. */
  onMapPerson: (personId: string) => void;
  /** Page-level search query — empty string means "no filter". */
  searchQuery: string;
}

function matchesQuery(person: IUnmappedPerson, query: string): boolean {
  if (query === '') return true;
  const needle = query.toLowerCase();
  if (person.displayName.toLowerCase().includes(needle)) return true;
  if (person.primaryEmail?.toLowerCase().includes(needle)) {
    return true;
  }
  return false;
}

function PersonMetaLine({ person }: Readonly<{ person: IUnmappedPerson }>) {
  const parts: string[] = [];
  if (person.primaryEmail !== null && person.primaryEmail.trim() !== '') {
    parts.push(person.primaryEmail);
  }
  if (person.jobTitle !== null && person.jobTitle.trim() !== '') {
    parts.push(person.jobTitle);
  }
  if (person.department !== null && person.department.trim() !== '') {
    parts.push(person.department);
  }
  if (parts.length === 0) return null;
  return (
    <p className="truncate text-xs text-muted-foreground" data-testid="unmapped-person-meta">
      {parts.join(' · ')}
    </p>
  );
}

export function UnmappedStrip({
  persons,
  onMapPerson,
  searchQuery,
}: Readonly<UnmappedStripProps>) {
  const [expanded, setExpanded] = useState(false);

  // Reset to collapsed when the list empties, so the banner restarts in its
  // default closed state once persons later refill (Rabizard #662 finding 2).
  // Without this, a user who expanded the banner, then dropped the list to
  // empty (banner hides), and then saw it return non-empty would find it
  // already open instead of collapsed-by-default.
  useEffect(() => {
    if (persons.length === 0) setExpanded(false);
  }, [persons.length]);

  const visiblePersons = useMemo(() => {
    const trimmed = searchQuery.trim();
    if (trimmed === '') return persons;
    return persons.filter((p) => matchesQuery(p, trimmed));
  }, [persons, searchQuery]);

  if (persons.length === 0) return null;

  const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon;

  return (
    <section
      className="rounded-md border border-warning bg-warning/10"
      aria-label="Unmapped people"
      data-testid="unmapped-strip"
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-warning-700">
          <span aria-hidden>{'⚠'}</span>
          <span>
            {`${persons.length} unmapped ${persons.length === 1 ? 'person' : 'people'}`}
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            (in Entra but not on any team)
          </span>
        </span>
        <span className="flex items-center gap-1 text-xs text-warning-700">
          {expanded ? 'Hide' : 'Show'}
          <Chevron className="h-4 w-4" aria-hidden />
        </span>
      </button>
      {expanded && (
        <div
          className="max-h-[40vh] space-y-2 overflow-y-auto border-t border-warning/40 px-4 py-3"
          data-testid="unmapped-strip-body"
        >
          {visiblePersons.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No unmapped people match the current search.
            </p>
          )}
          {visiblePersons.map((person) => (
            <article
              key={person.personId}
              className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2"
              data-testid="unmapped-person-card"
              data-person-id={person.personId}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{person.displayName}</p>
                <PersonMetaLine person={person} />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onMapPerson(person.personId)}
                data-testid="map-to-team-button"
              >
                Map to team {'→'}
              </Button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
